import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Lock } from "lucide-react";
import WorkVideo from "@/components/WorkVideo";
import AuthModal from "@/components/AuthModal";
import TermsGate from "@/components/TermsGate";
import { useAuth } from "@/context/AuthContext";

const SITES = [
  { id: "coffee", url: "coffee-shop.site", video: "/videos/coffee.mp4", poster: "/videos/coffee-poster.jpg", aspect: "2458 / 1410", tag: "Coffee shop — warm, neighborhood café brand with menu, story and ordering" },
  { id: "medspa", url: "med-spa.site", video: "/videos/medspa.mp4", poster: "/videos/medspa-poster.jpg", aspect: "2272 / 1268", tag: "Med spa — clinical calm, treatment menu and consultation booking" },
  { id: "realestate", url: "real-estate.site", video: "/videos/realestate.mp4", poster: "/videos/realestate-poster.jpg", aspect: "2272 / 1268", tag: "Real estate — listings-first layout with agent profile and inquiry flow" },
  { id: "vessels", url: "home-fragrance.site", video: "/videos/fourth.mp4", poster: "/videos/fourth-poster.jpg", aspect: "2272 / 1268", tag: "Home fragrance — editorial product collection with quiet, gallery-style pacing" },
];

export const WEBSITE_TIERS = [
  {
    id: "starter", name: "The Starter", price: "$499", featured: false,
    best: "A simple, polished starting point for new businesses.",
    items: ["Up to 3 pages", "Custom website design", "Mobile-friendly", "Contact form", "Social links", "Basic SEO", "1 revision", "Launch support"],
    cta: "Start Your Website", inquiry: "Website — The Starter ($499)", lookup_key: "website_starter",
  },
  {
    id: "signature", name: "The Signature", price: "$799", featured: true,
    best: "A more complete website for businesses ready to make a stronger impression.",
    items: ["Up to 5 pages", "Custom website design", "Mobile & tablet responsive", "Brand styling", "Contact & inquiry forms", "Basic SEO", "Social integration", "2 revision rounds", "Launch support"],
    cta: "Choose The Signature", inquiry: "Website — The Signature ($799)", lookup_key: "website_signature",
  },
  {
    id: "custom", name: "The Custom", price: "Starting at $1,200", featured: false,
    best: "For businesses looking for additional pages, features, or a more tailored experience.",
    items: ["Custom website design", "Shopify store builds", "6+ pages", "Advanced layouts", "Custom functionality", "SEO foundations", "Analytics setup", "Third-party integrations", "3 revision rounds", "Launch support"],
    cta: "Inquire About a Custom Build", inquiry: "Website — The Custom (from $1,200)",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function WebsitesPage() {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const pending = useRef(null);
  const [busyKey, setBusyKey] = useState("");
  const [pendingPlan, setPendingPlan] = useState(null);

  const buy = (t) => {
    if (!user) { pending.current = t; setBusyKey(t.lookup_key); setAuthOpen(true); return; }
    pending.current = null;
    setBusyKey("");
    setPendingPlan({ lookup_key: t.lookup_key, name: `Website — ${t.name}`, price: t.price, billing: "one-time" });
  };

  useEffect(() => {
    if (authOpen) return;
    if (user && pending.current) buy(pending.current);
    else if (!user && pending.current) { pending.current = null; setBusyKey(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authOpen]);

  return (
    <main className="min-h-screen bg-[#FAF6EF] blush-wash pt-32 pb-28" data-testid="websites-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="eyebrow">Our work</span>
          <h1 className="f-display text-4xl sm:text-5xl tracking-tight mt-4 max-w-3xl leading-[1.05]">
            Websites, built with <span className="f-serif text-[#A8785A]">intention.</span>
          </h1>
          <p className="text-[#8C7562] text-base leading-relaxed max-w-2xl mt-6">
            No templates. No shortcuts. Just thoughtful design, built from the ground up.
          </p>
          <p className="text-[#8C7562] text-base leading-relaxed max-w-2xl mt-4">
            Every website is crafted around your brand, refined to reflect your identity, and engineered for a seamless
            experience — from first impression to final conversion.
          </p>
          <p className="text-[#8C7562] text-base leading-relaxed max-w-2xl mt-4" data-testid="websites-shopify-note">
            We work with fully custom website builds as well as <span className="text-[#3A2E25] font-semibold">Shopify</span> website builds for
            businesses that sell products online.
          </p>
          <p className="text-[#57473B] text-base leading-relaxed max-w-2xl mt-4">Explore four recent creations below.</p>
        </motion.div>

        <div className="grid grid-cols-[minmax(0,1fr)] md:grid-cols-2 gap-8 mt-16">
          {SITES.map((site, i) => (
            <motion.div key={site.url} className="min-w-0" initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: (i % 2) * 0.12 }}>
              <WorkVideo site={site} />
              <p className="f-mono text-[10px] text-[#8C7562] mt-3 pl-1">{site.tag}</p>
            </motion.div>
          ))}
        </div>

        <section className="mt-28" data-testid="website-pricing">
          <motion.p {...fadeUp} className="eyebrow mb-4">Website packages</motion.p>
          <motion.h2 {...fadeUp} className="f-display text-3xl sm:text-4xl tracking-tight">Websites, built with intention.</motion.h2>
          <motion.p {...fadeUp} className="text-[#8C7562] text-sm max-w-2xl mt-4 mb-14 leading-relaxed">
            Thoughtfully designed websites for businesses ready to establish a polished online presence.
          </motion.p>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {WEBSITE_TIERS.map((t) => (
              <motion.div key={t.id} {...fadeUp}
                className={`rounded-2xl p-8 flex flex-col relative ${t.featured ? "border-2 border-[#A8785A] bg-[#FFF3F8] md:-translate-y-4 shadow-[0_16px_48px_rgba(168,120,90,0.18)]" : "border border-[#E8DCCB] bg-[#FFFDF7]"}`}
                data-testid={`website-tier-${t.id}`}>
                {t.featured && (
                  <span className="absolute -top-3.5 left-8 text-[9px] tracking-[0.25em] uppercase bg-[#A8785A] text-[#FFFDF7] font-semibold rounded-full px-4 py-1.5">Most chosen</span>
                )}
                <h3 className="f-display text-2xl tracking-tight">{t.name}</h3>
                <p className="f-display text-3xl text-[#A8785A] mt-4 mb-2">{t.price}</p>
                <p className="text-xs text-[#8C7562] mb-7 leading-relaxed">{t.best}</p>
                <ul className="flex flex-col gap-2.5 flex-1">
                  {t.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-[#57473B]">
                      <Check size={14} className="text-[#C48A9F] mt-0.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
                {t.lookup_key ? (
                  <div className="flex flex-col gap-2.5 mt-8">
                    <button onClick={() => buy(t)} disabled={busyKey === t.lookup_key}
                      className={`${t.featured ? "btn-primary" : "btn-ghost"} justify-center`} data-testid={`website-buy-${t.id}`}>
                      <Lock size={13} /> {busyKey === t.lookup_key ? "Opening checkout…" : `${t.cta} — ${t.price}`}
                    </button>
                    <Link to={`/social?tier=${encodeURIComponent(t.inquiry)}#inquire`}
                      className="text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8C7562] hover:text-[#3A2E25] transition-colors py-1"
                      data-testid={`website-tier-cta-${t.id}`}>
                      Ask a question first
                    </Link>
                  </div>
                ) : (
                  <Link to={`/social?tier=${encodeURIComponent(t.inquiry)}#inquire`}
                    className="btn-ghost justify-center mt-8 text-center" data-testid={`website-tier-cta-${t.id}`}>
                    {t.cta}
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-24 glass rounded-2xl p-10 sm:p-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8"
          data-testid="websites-cta">
          <div>
            <h2 className="f-display text-2xl sm:text-3xl tracking-tight">Have a project in mind?</h2>
            <p className="text-sm text-[#8C7562] mt-3 max-w-lg leading-relaxed">
              Tell us a little about your business through the inquiry form and select “Custom Website Build.” We’ll get
              back to you with the approach, timeline, and pricing for your project.
            </p>
          </div>
          <Link to="/social?tier=Custom%20Website%20Build#inquire" className="btn-primary shrink-0" data-testid="websites-inquire-btn">
            Start a Website Inquiry <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <TermsGate plan={pendingPlan} onClose={() => setPendingPlan(null)} />
    </main>
  );
}
